import type { ReactNode } from 'react';
import { ActivityIndicator, Alert, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ChevronRight, type LucideIcon } from 'lucide-react-native';
import { colors, fonts, radius, t, type Lang, type OwnerKey } from '@hogar/shared';
import { recentFailureReference } from '../lib/api';
import { openHelpEmail } from '../lib/help';
import { useSession } from '../lib/session';
import { showDialog } from './Dialogs';

/** Owner wording in the language the owner picked. */
export function useT() {
  const { lang } = useSession();
  return { lang, t: (key: OwnerKey, vars?: Record<string, string | number>) => t(lang, key, vars) };
}

type Weight = 'regular' | 'medium' | 'semibold';

export function Txt({
  children,
  weight = 'regular',
  size = 16,
  color = colors.text,
  style,
  numberOfLines,
  selectable,
}: {
  children: ReactNode;
  weight?: Weight;
  size?: number;
  color?: string;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
  selectable?: boolean;
}) {
  return (
    <Text numberOfLines={numberOfLines} selectable={selectable} style={[{ fontFamily: fonts[weight], fontSize: size, color, lineHeight: Math.round(size * 1.35) }, style]}>
      {children}
    </Text>
  );
}

export function Screen({ children, title, subtitle, right }: { children?: ReactNode; title?: string; subtitle?: string; right?: ReactNode }) {
  const insets = useSafeAreaInsets();
  return (
    <ScrollView style={styles.screen} keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingTop: insets.top + 18, paddingBottom: 32, paddingHorizontal: 16 }}>
      <View style={styles.inner}>
        {title !== undefined && (
          <View style={styles.titleRow}>
            <View style={{ flex: 1 }}>
              <Txt weight="semibold" size={26}>
                {title}
              </Txt>
              {subtitle ? (
                <Txt color={colors.textMuted} size={15} style={{ marginTop: 2 }}>
                  {subtitle}
                </Txt>
              ) : null}
            </View>
            {right}
          </View>
        )}
        {children}
      </View>
    </ScrollView>
  );
}

export function Card({ children, style }: { children: ReactNode; style?: StyleProp<ViewStyle> }) {
  return <View style={[styles.card, style]}>{children}</View>;
}

export function IconBox({ Icon, size = 44, tint = colors.primary, bg = colors.primarySoft, round }: { Icon: LucideIcon; size?: number; tint?: string; bg?: string; round?: boolean }) {
  return (
    <View style={[styles.iconBox, { width: size, height: size, backgroundColor: bg, borderRadius: round ? size / 2 : 12 }]}>
      <Icon size={Math.round(size * 0.5)} color={tint} strokeWidth={1.6} />
    </View>
  );
}

export function RowLink({
  Icon,
  iconTint,
  iconBg,
  round,
  title,
  subtitle,
  subtitleColor,
  badge,
  right,
  onPress,
  last,
}: {
  Icon?: LucideIcon;
  iconTint?: string;
  iconBg?: string;
  round?: boolean;
  title: string;
  subtitle?: string;
  subtitleColor?: string;
  badge?: number;
  right?: ReactNode;
  onPress?: () => void;
  last?: boolean;
}) {
  return (
    <Pressable onPress={onPress} disabled={!onPress} style={({ pressed }) => [styles.row, !last && styles.rowBorder, pressed && { opacity: 0.6 }]}>
      {Icon ? <IconBox Icon={Icon} tint={iconTint} bg={iconBg} round={round} /> : null}
      <View style={{ flex: 1 }}>
        <Txt weight="medium" size={16}>
          {title}
        </Txt>
        {subtitle ? (
          <Txt size={13} color={subtitleColor ?? colors.textMuted}>
            {subtitle}
          </Txt>
        ) : null}
      </View>
      {badge ? (
        <View style={styles.badge}>
          <Txt size={12} weight="semibold" color={colors.white}>
            {badge}
          </Txt>
        </View>
      ) : null}
      {right ?? (onPress ? <ChevronRight size={18} color={colors.textFaint} /> : null)}
    </Pressable>
  );
}

export function Button({
  label,
  onPress,
  Icon,
  variant = 'primary',
  disabled,
  style,
}: {
  label: string;
  onPress: () => void;
  Icon?: LucideIcon;
  variant?: 'primary' | 'outline' | 'danger';
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const primary = variant === 'primary';
  const fg = primary ? colors.white : variant === 'danger' ? colors.danger : colors.text;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.button,
        primary ? styles.buttonPrimary : styles.buttonOutline,
        pressed && { opacity: 0.8 },
        disabled && { opacity: 0.4 },
        style,
      ]}
    >
      {Icon ? <Icon size={18} color={fg} /> : null}
      <Txt weight="semibold" size={16} color={fg}>
        {label}
      </Txt>
    </Pressable>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Txt weight="semibold" size={16} style={{ marginTop: 22, marginBottom: 10 }}>
      {children}
    </Txt>
  );
}

export function Dot({ color }: { color: string }) {
  return <View style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: color }} />;
}

export const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.background },
  inner: { width: '100%', maxWidth: 560, alignSelf: 'center' },
  titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
  card: {
    backgroundColor: colors.card,
    borderRadius: radius.card,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    paddingHorizontal: 16,
    paddingVertical: 4,
    marginBottom: 12,
  },
  iconBox: { alignItems: 'center', justifyContent: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 12, minHeight: 64 },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: colors.divider },
  badge: { backgroundColor: colors.danger, minWidth: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 },
  button: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    borderRadius: radius.button,
    paddingHorizontal: 24,
  },
  buttonPrimary: { backgroundColor: colors.primary },
  buttonOutline: { backgroundColor: colors.card, borderWidth: 1.5, borderColor: colors.cardBorder },
});

export function Loading() {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background }}>
      <ActivityIndicator color={colors.primary} />
    </View>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <Txt weight="semibold" size={14} color={colors.textMuted} style={{ marginTop: 14, marginBottom: 6 }}>
      {children}
    </Txt>
  );
}

/** An error under a form, with the failed request's reference and a Get help link when there is one. */
export function ErrorText({ children }: { children?: ReactNode }) {
  const { t, lang } = useT();
  if (!children) return null;
  const reference = recentFailureReference();
  return (
    <View style={{ marginTop: 8, gap: 2 }}>
      <Txt size={14} color={colors.dangerText}>
        {children}
      </Txt>
      {reference && (
        <Txt size={13} color={colors.textMuted}>
          {t('errorReference', { ref: reference })} ·{' '}
          <Text onPress={() => openHelpEmail(lang, reference)} style={{ fontFamily: fonts.medium, fontSize: 13, color: colors.primary, textDecorationLine: 'underline' }}>
            {t('getHelp')}
          </Text>
        </Txt>
      )}
    </View>
  );
}

/** A problem worth a dialog: the message, its reference, and Get help (opens an email to support). */
export async function showProblem(message: string, lang: Lang) {
  const reference = recentFailureReference();
  const text = reference ? `${message}\n\n${t(lang, 'errorReference', { ref: reference })}` : message;
  if (await confirm(text, t(lang, 'getHelp'), 'OK')) openHelpEmail(lang, reference);
}

export function Field({
  value,
  onChange,
  placeholder,
  multiline,
  maxLength = 120,
  keyboardType,
  autoFocus,
  onSubmit,
  autoComplete,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
  keyboardType?: 'default' | 'email-address' | 'number-pad';
  autoFocus?: boolean;
  onSubmit?: () => void;
  autoComplete?: 'email' | 'one-time-code' | 'name' | 'off';
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={colors.textMuted}
      multiline={multiline}
      maxLength={maxLength}
      keyboardType={keyboardType}
      autoCapitalize={keyboardType === 'email-address' ? 'none' : 'sentences'}
      autoCorrect={keyboardType !== 'email-address'}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      onSubmitEditing={onSubmit}
      style={{
        fontFamily: fonts.regular,
        fontSize: 17,
        color: colors.text,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.cardBorder,
        borderRadius: 14,
        paddingHorizontal: 14,
        paddingVertical: 12,
        minHeight: multiline ? 110 : 52,
        textAlignVertical: multiline ? 'top' : 'center',
      }}
    />
  );
}

export function Chip({ label, on, onPress }: { label: string; on: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        minWidth: 48,
        minHeight: 42,
        paddingHorizontal: 12,
        borderRadius: 21,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: on ? colors.primary : colors.card,
        borderWidth: 1,
        borderColor: on ? colors.primary : colors.cardBorder,
      }}
    >
      <Txt size={14} weight="medium" color={on ? colors.white : colors.textMuted}>
        {label}
      </Txt>
    </Pressable>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <View style={{ flexDirection: 'row', backgroundColor: colors.card, borderRadius: 14, borderWidth: 1, borderColor: colors.cardBorder, padding: 4, gap: 4 }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <Pressable
            key={o.value}
            onPress={() => onChange(o.value)}
            style={{ flex: 1, minHeight: 44, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.primary : 'transparent', paddingHorizontal: 6 }}
          >
            <Txt size={14} weight={active ? 'semibold' : 'regular'} color={active ? colors.white : colors.textMuted} style={{ textAlign: 'center' }}>
              {o.label}
            </Txt>
          </Pressable>
        );
      })}
    </View>
  );
}

/** Yes-or-no question before something that cannot be undone. */
export function confirm(message: string, yes: string, no: string): Promise<boolean> {
  if (Platform.OS === 'web') return new Promise((resolve) => showDialog({ message, yes, no, resolve }));
  return new Promise((resolve) =>
    Alert.alert('', message, [
      { text: no, style: 'cancel', onPress: () => resolve(false) },
      { text: yes, style: 'destructive', onPress: () => resolve(true) },
    ]),
  );
}

export function notice(message: string) {
  if (Platform.OS === 'web') showDialog({ message, yes: 'OK', resolve: () => {} });
  else Alert.alert('', message);
}
